import { useState } from 'react';
import { useGT } from 'gt-react';
import {
  beginPolicyOverrideEdit,
  policyOverrideMutationHandlers,
} from '../components/policyOverrideMutationFeedback';
import {
  useCreateLeadTimeOverride,
  useUpdateLeadTimeOverride,
  type LeadTimeOverrideRow,
} from 'src/api/rpc/hooks/procurement/useLeadTimeOverrides';
import {
  useOverrideWindowState,
  type OverrideWindowState,
} from '../components/useOverrideWindowState';
import { daysInRange, parseDays } from './leadTimeOverrideRows';
import { runLeadTimeOverrideBulkCreate } from './runLeadTimeOverrideBulkCreate';

export interface LeadTimeOverrideFormTarget {
  warehouseId: string;
  productV3Key: string;
}

export interface LeadTimeOverrideFormState {
  days: string;
  setDays: (value: string) => void;
  window: OverrideWindowState;
  editing: LeadTimeOverrideRow | null;
  startEdit: (o: LeadTimeOverrideRow) => void;
  reset: () => void;
  submit: () => void;
  daysValue: number | null;
  daysValid: boolean;
  canSubmit: boolean;
  pending: boolean;
}

export function useLeadTimeOverrideForm(
  target: LeadTimeOverrideFormTarget,
  bulk?: {
    extraTargets: readonly LeadTimeOverrideFormTarget[];
    highlightedLineCount: number;
  },
): LeadTimeOverrideFormState {
  const gt = useGT();
  const createMutation = useCreateLeadTimeOverride();
  const updateMutation = useUpdateLeadTimeOverride();
  const windowState = useOverrideWindowState();

  const [days, setDays] = useState('');
  const [editing, setEditing] = useState<LeadTimeOverrideRow | null>(null);

  const daysValue = parseDays(days);
  const daysValid = daysValue !== null && daysInRange(daysValue);
  const pending = createMutation.isPending || updateMutation.isPending;
  const canSubmit =
    daysValid && windowState.isComplete && windowState.orderValid && !pending;

  function reset() {
    setEditing(null);
    setDays('');
    windowState.reset();
  }

  const { onSaved, onError } = policyOverrideMutationHandlers(reset, (e) =>
    gt('Failed to save total lead time override: {msg}', {
      context: 'lead time override error toast',
      msg: e.message,
    }),
  );

  function startEdit(o: LeadTimeOverrideRow) {
    beginPolicyOverrideEdit(
      o,
      setEditing,
      setDays,
      String(o.leadTimeDays),
      windowState,
    );
  }

  async function submit() {
    if (!canSubmit || daysValue === null) return;
    const payload = { leadTimeDays: daysValue, ...windowState.payload };
    if (editing) {
      updateMutation.mutate(
        { overrideId: editing.id, ...payload },
        {
          onSuccess: () =>
            onSaved(
              gt('Total lead time override updated', {
                context: 'lead time override toast',
              }),
            ),
          onError,
        },
      );
      return;
    }
    const targets = [
      target,
      ...(bulk?.extraTargets ?? []).filter(
        (t) =>
          t.productV3Key !== target.productV3Key ||
          t.warehouseId !== target.warehouseId,
      ),
    ];
    const { applied } = await runLeadTimeOverrideBulkCreate(
      gt,
      targets,
      bulk?.highlightedLineCount ?? 1,
      payload,
      (input) => createMutation.mutateAsync(input),
    );
    if (applied > 0) reset();
  }

  return {
    days,
    setDays,
    window: windowState,
    editing,
    startEdit,
    reset,
    submit,
    daysValue,
    daysValid,
    canSubmit,
    pending,
  };
}
